import React, { useState, useEffect } from 'react';
import axios from 'axios';
import FeatureGate from './FeatureGate';
import FeatureTiers from './FeatureTiers';
import { motion, AnimatePresence } from 'framer-motion';
import './components.css';

function FriendTraining({ userTier = 'guest', hasFeatureAccess, user }) {
  const [trainingSessions, setTrainingSessions] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [friendIds, setFriendIds] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Load training status on component mount
  useEffect(() => {
    loadTrainingStatus();
    loadPerformanceSummary();
  }, []);

  const loadTrainingStatus = async () => {
    try {
      const response = await axios.get('/api/friend-training/status');
      setTrainingSessions(response.data.active_sessions || []);
    } catch (err) {
      console.error('Error loading training status:', err);
    }
  };

  const loadPerformanceSummary = async () => {
    try {
      const response = await axios.get('/api/friend-training/performance');
      setPerformance(response.data);
    } catch (err) {
      console.error('Error loading performance:', err);
    }
  };

  const startFriendTraining = async (friendIdList = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const payload = {};
      if (friendIdList && friendIdList.length > 0) {
        payload.friend_ids = friendIdList;
      }

      const response = await axios.post('/api/friend-training/start', payload);
      
      if (response.data.session_id) {
        setSuccessMessage(response.data.message);
        setShowInviteModal(false);
        setFriendIds('');
        loadTrainingStatus();
        loadPerformanceSummary();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start friend training');
    } finally {
      setLoading(false);
    }
  };

  const stopTraining = async (sessionId) => {
    try {
      await axios.post('/api/friend-training/stop', { session_id: sessionId });
      setSuccessMessage('Training stopped successfully');
      loadTrainingStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to stop training');
    }
  };

  const renderViralFunnelMessage = () => {
    const messages = {
      guest: {
        title: "🚀 Your AI Tutor Gets Smarter With Friends!",
        subtitle: "Join friend training sessions to see how collaboration makes learning better",
        cta: "Experience Network Effects",
        limit: `You can join ${FeatureTiers.GUEST_LIMITS.FRIEND_TRAINING_SESSIONS} friend sessions (${FeatureTiers.GUEST_LIMITS.FRIEND_TRAINING_DURATION} min each)`
      },
      student: {
        title: "👥 Join Friend Training Sessions",
        subtitle: "Your friends can invite you to collaborative AI training",
        cta: "Join Friend Sessions",
        upgrade: "Upgrade to Unlimited to start your own friend training sessions!"
      },
      premium: {
        title: "🎯 Start Friend Training",
        subtitle: "Make your Neo model smarter by training with friends",
        cta: "Start Collaborative Training"
      },
      expert: {
        title: "📊 Advanced Friend Analytics",
        subtitle: "Deep insights into cross-training performance and network effects",
        cta: "Start Advanced Training"
      }
    };

    return messages[userTier] || messages.guest;
  };

  const renderNetworkEffectVisualizer = () => {
    if (!performance?.network_effect_benefits && trainingSessions.length === 0) {
      return null;
    }

    const sessionInfo = trainingSessions[0];
    const networkStrength = sessionInfo?.network_effect || 0;
    const friendsCount = sessionInfo?.friends?.length || 0;

    return (
      <motion.div 
        className="network-effect-card"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <h3>🌐 Network Effects Active</h3>
        <div className="network-metrics">
          <div className="metric">
            <div className="metric-value">{(networkStrength * 100).toFixed(0)}%</div>
            <div className="metric-label">Learning Speed Bonus</div>
          </div>
          <div className="metric">
            <div className="metric-value">{friendsCount}</div>
            <div className="metric-label">Active Friends</div>
          </div>
          <div className="metric">
            <div className="metric-value">{sessionInfo?.total_steps || 0}</div>
            <div className="metric-label">Training Steps</div>
          </div>
        </div>
        <div className="network-explanation">
          <p>Your Neo model is getting smarter through friend collaboration!</p>
          {friendsCount < 3 && (
            <p className="upgrade-hint">💡 Invite more friends for stronger network effects</p>
          )}
        </div>
      </motion.div>
    );
  };

  const renderPerformanceInsights = () => {
    if (!performance?.interpretation) return null;

    const { interpretation } = performance;

    return (
      <motion.div 
        className="performance-insights"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h3>📈 Your Progress</h3>
        <div className={`insight-card ${interpretation.improvement_level}`}>
          <div className="insight-message">{interpretation.improvement_message}</div>
          <div className="network-message">{interpretation.network_effect_message}</div>
          <div className="recommendation">
            <strong>Next Step:</strong> {interpretation.recommendation}
          </div>
          {interpretation.next_milestone && (
            <div className="milestone">
              <strong>Goal:</strong> {interpretation.next_milestone}
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  const renderUpgradePrompt = () => {
    if (userTier === 'premium' || userTier === 'expert' || userTier === 'unlimited' || userTier === 'home') return null;

    const prompt = userTier === 'guest'
      ? "🎓 Upgrade to Unlimited ($89/year) to join unlimited friend sessions!"
      : "🚀 Upgrade to Unlimited ($89/year) to start your own friend training sessions!";

    return (
      <motion.div 
        className="upgrade-prompt"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <div className="upgrade-content">
          <p>{prompt}</p>
          <button className="upgrade-button" onClick={() => window.location.href = '/pricing'}>
            View Pricing Plans
          </button>
        </div>
      </motion.div>
    );
  };

  const message = renderViralFunnelMessage();

  return (
    <div className="friend-training-container">
      <div className="friend-training-header">
        <h2>{message.title}</h2>
        <p className="subtitle">{message.subtitle}</p>
        {message.limit && <p className="limit-info">{message.limit}</p>}
      </div>

      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      {successMessage && (
        <motion.div 
          className="success-message"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
        >
          ✅ {successMessage}
        </motion.div>
      )}

      {/* Network Effect Visualizer */}
      {renderNetworkEffectVisualizer()}

      {/* Performance Insights */}
      {renderPerformanceInsights()}

      {/* Active Training Sessions */}
      {trainingSessions.length > 0 && (
        <div className="active-sessions">
          <h3>🔥 Active Friend Training</h3>
          {trainingSessions.map((session, index) => (
            <motion.div 
              key={session.session_id}
              className="session-card"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="session-info">
                <div className="session-id">Session: {session.session_id.split('_').pop()}</div>
                <div className="session-duration">
                  Running for {session.explanation?.duration_minutes || 0} minutes
                </div>
                <div className="session-friends">
                  👥 {session.friends?.length || 0} friends: {session.friends?.join(', ') || 'None'}
                </div>
                <div className="session-progress">
                  📊 {session.explanation?.progress_description || 'Starting up...'}
                </div>
                <div className="session-network">
                  🌐 {session.explanation?.network_effect_description || 'Building network effects...'}
                </div>
              </div>
              <button 
                onClick={() => stopTraining(session.session_id)}
                className="stop-button"
                disabled={loading}
              >
                Stop Training
              </button>
            </motion.div>
          ))}
        </div>
      )}

      {/* Start Training Controls */}
      <FeatureGate
        hasFeatureAccess={hasFeatureAccess}
        requiredFeature="FRIEND_TRAINING_HOST"
        userTier={userTier}
        fallback={
          <FeatureGate
            hasFeatureAccess={hasFeatureAccess}
            requiredFeature="FRIEND_TRAINING_JOIN"
            userTier={userTier}
            fallback={
              <FeatureGate
                hasFeatureAccess={hasFeatureAccess}
                requiredFeature="FRIEND_TRAINING_PREVIEW"
                userTier={userTier}
                fallback={renderUpgradePrompt()}
              >
                <div className="guest-actions">
                  <p>🎯 You can join friend training sessions when invited!</p>
                  <button 
                    onClick={() => startFriendTraining()}
                    disabled={loading}
                    className="join-training-button"
                  >
                    {loading ? 'Searching...' : 'Find Available Sessions'}
                  </button>
                </div>
              </FeatureGate>
            }
          >
            <div className="student-actions">
              <p>👥 You can join friend training sessions!</p>
              <button 
                onClick={() => startFriendTraining()}
                disabled={loading}
                className="join-training-button"
              >
                {loading ? 'Searching...' : 'Find Friend Sessions'}
              </button>
            </div>
          </FeatureGate>
        }
      >
        <div className="premium-actions">
          <button 
            onClick={() => setShowInviteModal(true)}
            disabled={loading}
            className="start-training-button"
          >
            {loading ? 'Starting...' : message.cta}
          </button>
          
          <button 
            onClick={() => startFriendTraining()}
            disabled={loading}
            className="auto-discover-button"
          >
            Auto-Discover Friends
          </button>
        </div>
      </FeatureGate>

      {/* Upgrade Prompt */}
      {renderUpgradePrompt()}

      {/* Friend Invitation Modal */}
      <AnimatePresence>
        {showInviteModal && (
          <motion.div 
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowInviteModal(false)}
          >
            <motion.div 
              className="modal-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3>🚀 Start Friend Training</h3>
              <p>Enter friend user IDs (comma-separated) or leave blank to auto-discover:</p>
              <textarea
                value={friendIds}
                onChange={(e) => setFriendIds(e.target.value)}
                placeholder="friend123, friend456, friend789"
                className="friend-ids-input"
              />
              <div className="modal-actions">
                <button 
                  onClick={() => {
                    const ids = friendIds.split(',').map(id => id.trim()).filter(id => id);
                    startFriendTraining(ids.length > 0 ? ids : null);
                  }}
                  disabled={loading}
                  className="confirm-button"
                >
                  {loading ? 'Starting...' : 'Start Training'}
                </button>
                <button 
                  onClick={() => setShowInviteModal(false)}
                  className="cancel-button"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx>{`
        .friend-training-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
        }

        .friend-training-header {
          text-align: center;
          margin-bottom: 30px;
        }

        .friend-training-header h2 {
          color: #2c3e50;
          margin-bottom: 10px;
        }

        .subtitle {
          color: #7f8c8d;
          font-size: 1.1em;
          margin-bottom: 10px;
        }

        .limit-info {
          color: #e67e22;
          font-weight: 500;
          font-size: 0.9em;
        }

        .network-effect-card {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-radius: 15px;
          padding: 25px;
          margin: 20px 0;
          box-shadow: 0 10px 30px rgba(0,0,0,0.2);
        }

        .network-effect-card h3 {
          margin-top: 0;
          text-align: center;
        }

        .network-metrics {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 20px;
          margin: 20px 0;
        }

        .metric {
          text-align: center;
        }

        .metric-value {
          font-size: 2em;
          font-weight: bold;
          color: #f1c40f;
        }

        .metric-label {
          font-size: 0.9em;
          opacity: 0.9;
        }

        .network-explanation {
          text-align: center;
          margin-top: 15px;
        }

        .network-explanation p {
          margin: 5px 0;
        }

        .upgrade-hint {
          color: #f39c12;
          font-weight: 500;
        }

        .performance-insights {
          background: #f8f9fa;
          border-radius: 10px;
          padding: 20px;
          margin: 20px 0;
        }

        .insight-card {
          padding: 15px;
          border-radius: 8px;
          border-left: 5px solid #3498db;
        }

        .insight-card.excellent {
          border-left-color: #27ae60;
          background: #d5f5d5;
        }

        .insight-card.good {
          border-left-color: #f39c12;
          background: #fff3cd;
        }

        .insight-card.learning {
          border-left-color: #3498db;
          background: #d1ecf1;
        }

        .insight-card.starting {
          border-left-color: #e74c3c;
          background: #f8d7da;
        }

        .insight-message, .network-message {
          margin: 10px 0;
          font-weight: 500;
        }

        .recommendation, .milestone {
          margin: 10px 0;
          font-size: 0.95em;
        }

        .active-sessions {
          margin: 30px 0;
        }

        .session-card {
          background: white;
          border: 2px solid #e0e0e0;
          border-radius: 10px;
          padding: 20px;
          margin: 15px 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 5px 15px rgba(0,0,0,0.1);
        }

        .session-info div {
          margin: 5px 0;
          font-size: 0.95em;
        }

        .session-id {
          font-weight: bold;
          color: #2c3e50;
        }

        .premium-actions, .student-actions, .guest-actions {
          display: flex;
          gap: 15px;
          justify-content: center;
          margin: 30px 0;
          flex-wrap: wrap;
        }

        .start-training-button, .join-training-button {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 15px 30px;
          border-radius: 25px;
          font-size: 1.1em;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 5px 15px rgba(0,0,0,0.2);
        }

        .start-training-button:hover, .join-training-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.3);
        }

        .auto-discover-button {
          background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
          color: white;
          border: none;
          padding: 15px 30px;
          border-radius: 25px;
          font-size: 1em;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .stop-button {
          background: #e74c3c;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 20px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .upgrade-prompt {
          background: linear-gradient(135deg, #f39c12 0%, #e67e22 100%);
          color: white;
          border-radius: 15px;
          padding: 25px;
          text-align: center;
          margin: 30px 0;
        }

        .upgrade-button {
          background: white;
          color: #e67e22;
          border: none;
          padding: 12px 25px;
          border-radius: 20px;
          font-weight: 600;
          cursor: pointer;
          margin-top: 15px;
          transition: all 0.3s ease;
        }

        .upgrade-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(0,0,0,0.2);
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.7);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 15px;
          padding: 30px;
          max-width: 500px;
          width: 90%;
          max-height: 80vh;
          overflow-y: auto;
        }

        .friend-ids-input {
          width: 100%;
          min-height: 80px;
          padding: 15px;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          font-size: 1em;
          margin: 15px 0;
          resize: vertical;
        }

        .modal-actions {
          display: flex;
          gap: 15px;
          justify-content: center;
          margin-top: 20px;
        }

        .confirm-button {
          background: #27ae60;
          color: white;
          border: none;
          padding: 12px 25px;
          border-radius: 20px;
          cursor: pointer;
          font-weight: 600;
        }

        .cancel-button {
          background: #95a5a6;
          color: white;
          border: none;
          padding: 12px 25px;
          border-radius: 20px;
          cursor: pointer;
        }

        .error-message {
          background: #f8d7da;
          color: #721c24;
          padding: 15px;
          border-radius: 8px;
          margin: 15px 0;
          border: 1px solid #f5c6cb;
        }

        .success-message {
          background: #d4edda;
          color: #155724;
          padding: 15px;
          border-radius: 8px;
          margin: 15px 0;
          border: 1px solid #c3e6cb;
        }

        @media (max-width: 768px) {
          .friend-training-container {
            padding: 15px;
          }
          
          .network-metrics {
            grid-template-columns: 1fr;
            gap: 15px;
          }
          
          .session-card {
            flex-direction: column;
            gap: 15px;
          }
          
          .premium-actions, .student-actions, .guest-actions {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
}

export default FriendTraining;